import * as TablerIcons from "@tabler/icons-react";
import type { IconProps } from "@tabler/icons-react";
import type {
    ForwardRefExoticComponent,
    RefAttributes,
} from "react";
import CustomTooltip from "../ui/CustomTooltip/CustomTooltip";
import React, { useState } from "react";
import { ActionIcon } from "@mantine/core";

interface IconItemProps {
    readonly value: string;
    readonly iconColor?: string;
    readonly hoverColor?: string;
    readonly isHover?: boolean;
    readonly width?: string | number;
    readonly height?: string | number;
    readonly strokeWidth?: string | number;
    readonly disabled?: boolean;
    style?: React.CSSProperties;
    onClick?: () => void;
}

function IconItem({ value, iconColor, hoverColor, isHover, width, height, strokeWidth, disabled = false, style, onClick,
}: IconItemProps) {
    const IconComponent = TablerIcons[`Icon${value}` as keyof typeof TablerIcons] as | ForwardRefExoticComponent<
        IconProps & RefAttributes<SVGSVGElement>
    >
        | undefined;

    const [isHovered, setIsHovered] = useState(false);

    if (!IconComponent) {
        return null;
    }

    return (
        <IconComponent
            color={
                disabled
                    ? iconColor || "gray"
                    : isHovered
                        ? hoverColor || "var(--mantine-color-anchor)"
                        : iconColor || "gray"
            }
            stroke={strokeWidth || 1.5}
            width={width || "1.2rem"}
            height={height || "1.2rem"}
            style={{
                cursor: disabled
                    ? "not-allowed"
                    : isHover
                        ? "pointer"
                        : "default",
                opacity: disabled ? 0.5 : 1,
                ...style,
            }}
            onMouseEnter={() => {
                if (isHover && !disabled) {
                    setIsHovered(true);
                }
            }}
            onMouseLeave={() => {
                setIsHovered(false);
            }}
            onClick={() => {
                if (!disabled) {
                    onClick?.();
                }
            }}
        />
    );
}

interface IconSelectorProps {
    iconName: string;
    iconColor?: string;
    hoverColor?: string;
    toolTipLabel?: string;
    isHover?: boolean;
    width?: string | number;
    height?: string | number;
    strokeWidth?: string | number;
    style?: React.CSSProperties;
      onClick?: () => void;
    disabled?: boolean;
    withBox?: boolean;
    withBoxBorder?: boolean;
    boxVariant?: string;
    boxSize?: string;
    boxColor?: string;
    boxRadius?: string;
    boxBorderColor?: string;
    mt?: number;
    autoLightBoxColor?: boolean;
    useIconColorAsBoxBorder?: boolean;
}

const DisplayIcon = ({
    iconName,
    iconColor,
    hoverColor,
    toolTipLabel,
    isHover,
    width,
    height,
    strokeWidth,
    disabled = false,
    style,
    onClick,
    withBox = false,
    withBoxBorder = true,
    boxVariant = "filled",
    boxSize = "lg",
    boxColor = "#f8fafc",
    boxRadius,
    boxBorderColor = "#e2e8f0",
    autoLightBoxColor = false,
    useIconColorAsBoxBorder = false,
    mt,
}: IconSelectorProps) => {
    const getLightColor = (color: string): string => {
        if (!color) {
            return "#f8fafc";
        }
        if (/^#[0-9A-Fa-f]{6}$/.test(color)) {
            const r = parseInt(color.slice(1, 3), 16);
            const g = parseInt(color.slice(3, 5), 16);
            const b = parseInt(color.slice(5, 7), 16);
            const lightR = Math.round(r + (255 - r) * 0.88);
            const lightG = Math.round(g + (255 - g) * 0.88);
            const lightB = Math.round(b + (255 - b) * 0.88);
            return `rgb(${lightR}, ${lightG}, ${lightB})`;
        }

        return "#f8fafc";
    };
    const resolvedBoxColor = autoLightBoxColor && iconColor ? getLightColor(iconColor) : boxColor;
    const finalBoxBorderColor =
        useIconColorAsBoxBorder && iconColor
            ? getLightColor(iconColor)
            : boxBorderColor;
    const iconContent = (
        <IconItem
            value={iconName || ""}
            iconColor={iconColor}
            hoverColor={hoverColor}
            isHover={isHover}
            disabled={disabled}
            width={width}
            height={height}
            strokeWidth={strokeWidth}
            style={style}
            onClick={withBox ? undefined : onClick}
        />
    );

    if (withBox) {
        return (
            <ActionIcon
                variant={boxVariant}
                size={boxSize}
                color={resolvedBoxColor}
                disabled={disabled}
                style={{
                    border: withBoxBorder
                        ? `1px solid ${finalBoxBorderColor}`
                        : undefined,
                    marginTop: mt,
                }}
                radius={boxRadius}
                onClick={onClick}
            >
                {toolTipLabel ? (
                    <CustomTooltip label={toolTipLabel}>
                        <div>{iconContent}</div>
                    </CustomTooltip>
                ) : (
                    iconContent
                )}
            </ActionIcon>
        );
    }

    return (
        <div
            style={{
                marginTop: mt,
                cursor: "pointer",
                display: "inline-flex",
            }}
        >
            {toolTipLabel ? (
                <CustomTooltip label={toolTipLabel}>
                    <div>{iconContent}</div>
                </CustomTooltip>
            ) : (
                iconContent
            )}
        </div>
    );
};

export default DisplayIcon;